import './BusinessPage.css';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getBusiness from '@src/APIs/graphQl/Business/getBusiness';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import ProjectsCard from '@src/App/Components/Cards/EntityCards/Projects/ProjectsCard/ProjectsCard';
import EntityPageInformation from '@src/App/Components/Core/Layouts/LayoutElements/EntityPageInformation/EntityPageInformation';
import { BusinessDialog } from '@src/App/Components/Dialogs/BusinessDialog/BusinessDialog';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import ExpandableText from '@src/App/Components/General/ExpandableText/ExpandableText';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import type Business from '@src/Objects/Business';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import EntityPage from '../../Generic/EntityPage/EntityPage';

interface BusinessPageProps {
    entity?: Business;
    inDialog?: boolean;
}

/**
 * BusinessPage component.
 *
 * @param {BusinessPageProps} props - The props object containing the required data for the BusinessPage.
 * @returns {JSX.Element} The rendered BusinessPage component.
 */
function BusinessPage(props: BusinessPageProps) {
    const navigate = useNavigate();
    const { openDialog } = useDialog();
    const { id } = useParams();
    const entityId = props.entity ? props.entity.id : id ? Number.parseInt(id) : -1;
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).businesses;
    const permissions = useSelector((state: AppState) => state.permissions);
    const entityState = useSelector((state: AppState) => state.businesses.find((a) => a.id === entityId));

    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);

    useEffect(() => {
        if (props.inDialog) return;
        getBusiness(entityId).catch((ex) => {
            setLoadingError(ex);
        });
    }, [entityId, props.inDialog]);

    const handleOpenEditDialog = async () => {
        return openDialog(<BusinessDialog id="BusinessDialog" business={entityState} />);
    };
    return (
        <EntityPage
            inDialog={props.inDialog}
            entity={entityState}
            loadingError={loadingError}
            admin={permissions.admin}
            permissions={permissions.entities.Companies}
            onDelete={() => {
                if (!props.inDialog) navigate(`/${ELinks.SALES_CUSTOMERS}`);
            }}
            openEditDialog={handleOpenEditDialog}
            informations={
                entityState
                    ? [
                          entityState.description ? (
                              <EntityPageInformation title={langStrings.description} size={'full'}>
                                  <ExpandableText>
                                      <FormatedTextInput value={entityState.description} readonly={true} />
                                  </ExpandableText>
                              </EntityPageInformation>
                          ) : undefined,
                      ]
                    : []
            }
        >
            <>{entityState && <ProjectsCard projects={entityState.projects} />}</>
        </EntityPage>
    );
}

export default BusinessPage;
